
import React, { useState } from "react";
import { PanelLeftOpen, PanelRightOpen, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSidebarLogic } from "./hooks/useSidebarLogic";
import SidebarLogo from "./shared/SidebarLogo";
import SidebarNavigation from "./shared/SidebarNavigation";
import SidebarUser from "./shared/SidebarUser";

import TokenBalance from "../tokens/TokenBalance";
import TokenPurchaseModal from "../tokens/TokenPurchaseModal";
import TokenDepletionModal from "../tokens/TokenDepletionModal";
import { useTokens } from "../../hooks/useTokens";

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDepletionModal, setShowDepletionModal] = useState(false);
  const { user, isActivePath, handleLogout, handleNewChat } = useSidebarLogic();
  const { tokenBalance } = useTokens();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handlePurchaseClick = () => {
    setShowDepletionModal(false);
    setShowPurchaseModal(true);
  };

  const handleClosePurchaseModal = () => {
    setShowPurchaseModal(false);
  };

  const handleCloseDepletionModal = () => {
    setShowDepletionModal(false);
  };

  return (
    <>
      <div 
        className={`h-full shadow-[0_4px_6px_-1px_rgba(10,13,18,0.1),0_2px_4px_-2px_rgba(10,13,18,0.06)] flex flex-col relative bg-sidebar transition-all duration-300 ${
          isCollapsed ? "w-[80px]" : "w-[280px]"
        } hidden md:flex`}
      >
        <SidebarLogo isCollapsed={isCollapsed} />

        {/* New Chat Button - Only show when logged in */}
        {/* {!isCollapsed && user && (
          <div className="px-8 mb-4">
            <Button 
              onClick={handleNewChat}
              variant="default"
              className="w-full py-3 px-6"
              size="sm"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Chat
            </Button>
          </div>
        )} */}

        <div className={`flex flex-col mt-6 ${isCollapsed ? "px-4" : "px-8"}`}>
          <SidebarNavigation 
            isActivePath={isActivePath}
            isCollapsed={isCollapsed}
            user={user}
          />
        </div>

        <div className="flex-grow"></div>

        {/* Toggle sidebar button */}
        <div className={`mb-4 ${isCollapsed ? "px-4" : "px-8"}`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={toggleSidebar} 
                  className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2"} py-3 px-4 rounded-md transition-colors hover:bg-sidebar-accent w-full`}
                  aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {isCollapsed ? (
                    <PanelLeftOpen className="w-6 h-6 text-sidebar-foreground" />
                  ) : (
                    <PanelRightOpen className="w-5 h-5 text-sidebar-foreground" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>


        {/* Token Balance - Only show when logged in */}
        {user && (
          <TokenBalance 
            isCollapsed={isCollapsed}
            onPurchaseClick={() => setShowPurchaseModal(true)}
          />
        )}

        <SidebarUser 
          user={user}
          onLogout={handleLogout}
          isCollapsed={isCollapsed}
        />
      </div>

      {/* Token Purchase Modal */}
      <TokenPurchaseModal
        isOpen={showPurchaseModal}
        onClose={handleClosePurchaseModal}
        currentBalance={tokenBalance}
      />

      {/* Token Depletion Modal */}
      <TokenDepletionModal
        isOpen={showDepletionModal}
        onClose={handleCloseDepletionModal}
        onPurchaseClick={handlePurchaseClick}
      />
    </>
  );
};

export default Sidebar;
